import { Lock } from "lucide-react";
import type React from "react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { usePrivateNotes } from "../../hooks/usePrivateNotes";
import { Button } from "../ui/button";
import { Input } from "../ui/input";

interface PrivateNotesUnlockFormProps {
  /** true: ポップオーバー内などの狭い場所向けのコンパクト表示 */
  compact?: boolean;
}

/**
 * 暗号化プライベートメモのロック解除フォーム
 * メモポップオーバー（ロック中表示）とメモ管理ダイアログの両方で使う
 */
export function PrivateNotesUnlockForm({ compact = false }: PrivateNotesUnlockFormProps) {
  const { t } = useTranslation();
  const { unlock } = usePrivateNotes();
  const [passphrase, setPassphrase] = useState("");
  const [hasError, setHasError] = useState(false);
  const [isUnlocking, setIsUnlocking] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (!passphrase || isUnlocking) return;

    setIsUnlocking(true);
    setHasError(false);
    try {
      await unlock(passphrase);
      setPassphrase("");
    } catch {
      // 誤パスフレーズ（PrivateNotesDecryptionError）。データは不変
      setHasError(true);
    } finally {
      setIsUnlocking(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className={compact ? "space-y-1.5" : "space-y-2"}>
      <div className="flex items-center gap-1.5">
        <Input
          type="password"
          value={passphrase}
          onChange={(e) => {
            setPassphrase(e.target.value);
            setHasError(false);
          }}
          placeholder={t("privateMemo.encryption.passphrasePlaceholder")}
          autoComplete="current-password"
          aria-label={t("privateMemo.encryption.passphrasePlaceholder")}
          className={compact ? "h-7 text-xs" : "h-9 text-sm"}
        />
        <Button
          type="submit"
          size="sm"
          disabled={!passphrase || isUnlocking}
          className={compact ? "h-7 text-xs px-2 flex-shrink-0" : "flex-shrink-0"}
        >
          <Lock className="w-3 h-3 mr-1" />
          {isUnlocking
            ? t("privateMemo.encryption.unlocking")
            : t("privateMemo.encryption.unlockButton")}
        </Button>
      </div>
      {hasError && (
        <p className={`text-red-600 ${compact ? "text-[10px]" : "text-xs"}`} role="alert">
          {t("privateMemo.encryption.wrongPassphrase")}
        </p>
      )}
    </form>
  );
}
